import { useState, useEffect } from "react";
import { CheckCircle2, ArrowRight, Download, CreditCard, Plus, Trash2, Edit, AlertCircle, Check, ExternalLink, Shield, Zap, Building2, X, Loader2, Activity, ShoppingCart } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useLanguage } from "@/context/LanguageContext";
import { useAuth } from "@/context/AuthContext";
import { usersApi, tokenStore } from "@/lib/api";
import { toast } from "sonner";
import { HelpIcon } from "@/components/HelpIcon";

const API = process.env.REACT_APP_BACKEND_URL;

// ── PQC Usage Card (inline component) ─────────────────────────────────────────
function PQCUsageCard({ token }) {
  const [usage, setUsage]       = useState(null);
  const [bundles, setBundles]   = useState([]);
  const [buying, setBuying]     = useState(null);
  const [loading, setLoading]   = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const [u, b] = await Promise.all([
          fetch(`${API}/api/v1/billing/pqc-usage`,             { headers: { Authorization: `Bearer ${token}` } }).then(r => r.ok ? r.json() : null),
          fetch(`${API}/api/v1/billing/pqc-credits/bundles`,   { headers: { Authorization: `Bearer ${token}` } }).then(r => r.ok ? r.json() : null),
        ]);
        setUsage(u);
        setBundles(b?.bundles || []);
      } catch {}
      setLoading(false);
    })();
  }, [token]);

  // Poll returning Stripe session for PQC credits
  useEffect(() => {
    const params    = new URLSearchParams(window.location.search);
    const sessionId = params.get("pqc_session_id");
    if (!sessionId) return;
    const url = new URL(window.location.href);
    url.searchParams.delete("pqc_session_id");
    window.history.replaceState({}, "", url.toString());

    let attempts = 0;
    const poll = async () => {
      try {
        const res  = await fetch(`${API}/api/v1/billing/pqc-credits/checkout/status/${sessionId}`, { headers: { Authorization: `Bearer ${token}` } });
        const data = await res.json();
        if (data.payment_status === "paid") { toast.success(`${data.credits?.toLocaleString()} PQC credits added!`); return; }
        if (data.status === "expired")      { toast.error("Session expired"); return; }
        if (attempts++ < 5) setTimeout(poll, 2000);
      } catch { if (attempts++ < 5) setTimeout(poll, 2000); }
    };
    poll();
  }, [token]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleBuyBundle = async (bundle) => {
    setBuying(bundle.id);
    try {
      const res  = await fetch(`${API}/api/v1/billing/pqc-credits/checkout`, {
        method:  "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body:    JSON.stringify({ bundle_id: bundle.id, origin_url: window.location.origin }),
      });
      if (!res.ok) { const e = await res.json(); throw new Error(e.detail || "Checkout failed"); }
      const { url } = await res.json();
      // Use pqc_session_id param to differentiate from plan session_id
      window.location.href = url.replace(
        "?session_id=",
        "?session_id="
      ).replace(/\?session_id=([^&]+)/, (_, sid) => `?pqc_session_id=${sid}`);
    } catch (err) {
      toast.error(err.message || "Checkout failed");
    }
    setBuying(null);
  };

  const ALGO_COLORS = {
    "ML-KEM-768":          "#3b82f6",
    "ML-DSA-65":           "#7c3aed",
    "SLH-DSA-SHAKE-256s":  "#059669",
    "SLH-DSA-SHAKE-128s":  "#0284c7",
  };

  return (
    <div className="bg-white border border-slate-200 mb-8" data-testid="pqc-usage-card">
      <div className="flex items-center justify-between px-6 py-4 border-b border-slate-200">
        <div>
          <div className="flex items-center gap-2">
            <Activity className="w-4 h-4 text-indigo-600" />
            <h3 className="text-sm font-semibold text-slate-900">PQC API Usage</h3>
          </div>
          <p className="text-xs text-slate-500 mt-0.5">Post-Quantum Cryptography calls this month</p>
        </div>
        {usage && (
          <span className="text-xs bg-slate-100 text-slate-600 px-2 py-1 rounded font-mono font-medium">
            {usage.total_calls?.toLocaleString()} calls in {usage.month}
          </span>
        )}
      </div>

      <div className="px-6 py-4">
        {loading ? (
          <div className="flex items-center gap-2 text-slate-400 text-sm py-2">
            <Loader2 className="w-4 h-4 animate-spin" /> Loading usage...
          </div>
        ) : !usage || usage.total_calls === 0 ? (
          <p className="text-sm text-slate-400 py-2">No PQC API calls this month. Start using ML-KEM, ML-DSA, or SLH-DSA.</p>
        ) : (
          <div className="grid md:grid-cols-2 gap-6">
            {/* Algorithm breakdown */}
            <div>
              <div className="text-[10px] uppercase tracking-wider text-slate-400 font-medium mb-3">By Algorithm</div>
              <div className="space-y-2">
                {usage.by_algorithm?.map(({ algorithm, count }) => {
                  const pct   = usage.total_calls > 0 ? Math.round((count / usage.total_calls) * 100) : 0;
                  const color = ALGO_COLORS[algorithm] || "#94a3b8";
                  return (
                    <div key={algorithm} data-testid={`pqc-usage-algo-${algorithm}`}>
                      <div className="flex justify-between text-xs mb-1">
                        <span className="text-slate-600 font-medium">{algorithm}</span>
                        <span className="font-mono text-slate-500">{count} ({pct}%)</span>
                      </div>
                      <div className="h-1.5 bg-slate-100 rounded-full overflow-hidden">
                        <div className="h-full rounded-full transition-all" style={{ width: `${pct}%`, backgroundColor: color }} />
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Operation breakdown */}
            <div>
              <div className="text-[10px] uppercase tracking-wider text-slate-400 font-medium mb-3">By Operation</div>
              <div className="grid grid-cols-2 gap-2">
                {usage.by_operation?.map(({ operation, count }) => (
                  <div key={operation} className="bg-slate-50 border border-slate-100 rounded p-2.5">
                    <div className="text-lg font-bold font-mono text-slate-900">{count}</div>
                    <div className="text-[10px] text-slate-500 capitalize mt-0.5">{operation}</div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* Credits section */}
        {usage && (
          <div className="mt-4 pt-4 border-t border-slate-100">
            <div className="flex items-center justify-between mb-3">
              <div className="text-xs font-medium text-slate-700">
                Purchased Credits: <span className="font-mono text-indigo-600">{usage.remaining_credits?.toLocaleString() || 0}</span> remaining
                {usage.purchased_credits > 0 && <span className="text-slate-400 ml-1">of {usage.purchased_credits?.toLocaleString()}</span>}
              </div>
            </div>
            {/* Buy bundles */}
            <div className="flex flex-wrap gap-2">
              {bundles.map(bundle => (
                <button
                  key={bundle.id}
                  onClick={() => handleBuyBundle(bundle)}
                  disabled={!!buying}
                  className="flex items-center gap-1.5 text-xs px-3 py-1.5 border border-slate-200 text-slate-700 hover:bg-slate-50 hover:border-slate-300 transition-colors rounded font-medium disabled:opacity-50"
                  data-testid={`buy-bundle-${bundle.id}`}
                >
                  {buying === bundle.id ? <Loader2 className="w-3 h-3 animate-spin" /> : <ShoppingCart className="w-3 h-3" />}
                  {bundle.label} — ${bundle.price}
                </button>
              ))}
            </div>
            <p className="text-[10px] text-slate-400 mt-2">Credits never expire. Purchased credits supplement your plan's monthly allowance.</p>
          </div>
        )}
      </div>
    </div>
  );
}
const USAGE_METERS = [
  { label: "API Calls", used: 847168, total: 1000000, unit: "calls" },
  { label: "Bandwidth", used: 38.4, total: 50, unit: "GB" },
  { label: "VRF Requests", used: 189234, total: 500000, unit: "requests" },
];

const CARD_BRAND_LOGOS = {
  visa: "https://upload.wikimedia.org/wikipedia/commons/thumb/5/5e/Visa_Inc._logo.svg/200px-Visa_Inc._logo.svg.png",
  mastercard: "https://upload.wikimedia.org/wikipedia/commons/thumb/2/2a/Mastercard-logo.svg/200px-Mastercard-logo.svg.png",
  amex: "https://upload.wikimedia.org/wikipedia/commons/thumb/f/fa/American_Express_logo_%282018%29.svg/200px-American_Express_logo_%282018%29.svg.png",
};

export default function Billing() {
  const { t } = useLanguage();
  const { user } = useAuth();
  const [addPaymentOpen, setAddPaymentOpen] = useState(false);
  const [changePlanOpen, setChangePlanOpen] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [paymentMethods, setPaymentMethods] = useState([]);
  const [cardNumber, setCardNumber] = useState("");
  const [cardExpiry, setCardExpiry] = useState("");
  const [cardCvc, setCardCvc] = useState("");
  const [cardName, setCardName] = useState("");
  const [billingAddress, setBillingAddress] = useState("");
  const [plans, setPlans] = useState([]);
  const [plansLoading, setPlansLoading] = useState(true);
  const [checkoutLoading, setCheckoutLoading] = useState(false);
  const [payingPlan, setPayingPlan] = useState(null);
  const [subscription, setSubscription] = useState(null);
  const [invoices, setInvoices] = useState([]);
  const [invoicesLoading, setInvoicesLoading] = useState(true);
  const [portalLoading, setPortalLoading] = useState(false);

  // Check for returning Stripe session
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const sessionId = params.get("session_id");
    if (!sessionId) return;
    // Clean URL
    const url = new URL(window.location.href);
    url.searchParams.delete("session_id");
    window.history.replaceState({}, "", url.toString());

    let attempts = 0;
    toast.info("Verifying payment...");
    const poll = async () => {
      try {
        const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/v1/billing/checkout/status/${sessionId}`, {
          headers: { Authorization: `Bearer ${tokenStore.getAccess()}` },
        });
        if (!res.ok) throw new Error("Status check failed");
        const data = await res.json();
        if (data.payment_status === "paid") {
          toast.success(`Payment confirmed! Your plan has been upgraded to ${data.plan_name}.`);
          return;
        }
        if (data.status === "expired") { toast.error("Payment session expired."); return; }
        if (attempts++ < 5) setTimeout(poll, 2000);
      } catch { if (attempts++ < 5) setTimeout(poll, 2000); }
    };
    poll();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Stripe Billing Portal handler
  const handleBillingPortal = async () => {
    setPortalLoading(true);
    try {
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/v1/billing/portal`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${tokenStore.getAccess()}` },
        body: JSON.stringify({ return_url: window.location.href }),
      });
      // Read body defensively — Playwright/proxy environments can pre-consume the stream
      let data = null;
      try { data = await res.json(); } catch { /* body already consumed by intermediary */ }
      if (!res.ok) {
        const msg = data?.detail
          || (res.status === 404 ? "No active subscription found. Please upgrade your plan first." : "Could not open billing portal");
        throw new Error(msg);
      }
      window.location.href = data.url;
    } catch (err) {
      toast.error(err.message || "Billing portal unavailable");
    } finally {
      setPortalLoading(false);
    }
  };

  // Stripe checkout handler
  const handleUpgrade = async (planSlug) => {
    if (!planSlug) return;
    const plan = displayPlans.find(p => p.id === planSlug);
    if (plan?.current) { toast.info("You are already on this plan."); return; }
    if (plan?.price === null) { toast.info("Please contact sales for Enterprise pricing."); return; }
    if (plan?.price === 0) { toast.info("This plan is free — no payment needed."); return; }
    setCheckoutLoading(true);
    setPayingPlan(planSlug);
    try {
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/v1/billing/checkout`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${tokenStore.getAccess()}` },
        body: JSON.stringify({ plan_slug: planSlug, origin_url: window.location.origin }),
      });
      if (!res.ok) { const e = await res.json(); throw new Error(e.detail || "Checkout failed"); }
      const { url } = await res.json();
      window.location.href = url;
    } catch (err) {
      toast.error(err.message || "Could not start checkout");
      setCheckoutLoading(false);
      setPayingPlan(null);
    }
  };

  // Fetch subscription (current plan)
  useEffect(() => {
    (async () => {
      try {
        const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/v1/billing/subscription`, {
          headers: { Authorization: `Bearer ${tokenStore.getAccess()}` },
        });
        if (res.ok) setSubscription(await res.json());
      } catch { /* keep null */ }
    })();
  }, []);

  // Fetch real invoices from payment_transactions
  useEffect(() => {
    (async () => {
      try {
        const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/v1/billing/invoices`, {
          headers: { Authorization: `Bearer ${tokenStore.getAccess()}` },
        });
        if (res.ok) setInvoices(await res.json());
      } catch { /* keep empty */ }
      finally { setInvoicesLoading(false); }
    })();
  }, []);

  // Fetch real plans from backend
  useEffect(() => {
    (async () => {
      try {
        const { usersApi: _u, ...rest } = await import("@/lib/api");
        // Use the api client directly
        const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/v1/plans/public`, {
          headers: { "Authorization": `Bearer ${(await import("@/lib/api")).tokenStore.getAccess()}` }
        });
        if (res.ok) {
          const data = await res.json();
          setPlans(data);
        }
      } catch { /* use empty — plans will still show UI */ }
      finally { setPlansLoading(false); }
    })();
  }, []);

  // Map API plan to billing display format — use real subscription from backend
  const currentPlanSlug = subscription?.plan_slug || "free";
  const displayPlans = plans.map(p => ({
    id: p.slug,
    name: p.name,
    price: p.slug === "enterprise" ? null : p.price_monthly,  // null = Custom pricing
    desc: p.description,
    features: p.features,
    current: p.slug === currentPlanSlug,
    badge: p.badge,
  }));

  const formatCardNumber = (value) => {
    const v = value.replace(/\s+/g, '').replace(/[^0-9]/gi, '');
    const matches = v.match(/\d{4,16}/g);
    const match = matches && matches[0] || '';
    const parts = [];
    for (let i = 0, len = match.length; i < len; i += 4) {
      parts.push(match.substring(i, i + 4));
    }
    return parts.length ? parts.join(' ') : value;
  };

  const formatExpiry = (value) => {
    const v = value.replace(/\s+/g, '').replace(/[^0-9]/gi, '');
    if (v.length >= 2) {
      return v.substring(0, 2) + '/' + v.substring(2, 4);
    }
    return v;
  };

  const handleSetDefault = (id) => {
    setPaymentMethods(prev => prev.map(pm => ({ ...pm, isDefault: pm.id === id })));
  };

  const handleDeletePayment = (id) => {
    setPaymentMethods(prev => prev.filter(pm => pm.id !== id));
  };

  const handleAddCard = () => {
    // Simulate adding a card
    const newCard = {
      id: `pm_${Date.now()}`,
      type: "card",
      brand: "visa",
      last4: cardNumber.slice(-4),
      expMonth: parseInt(cardExpiry.split('/')[0]),
      expYear: 2020 + parseInt(cardExpiry.split('/')[1]),
      isDefault: paymentMethods.length === 0,
    };
    setPaymentMethods(prev => [...prev, newCard]);
    setAddPaymentOpen(false);
    setCardNumber("");
    setCardExpiry("");
    setCardCvc("");
    setCardName("");
  };

  return (
    <div data-testid="billing-page">
      <div className="mb-8">
        <div className="flex items-center gap-2">
          <h1 className="text-2xl font-bold text-slate-900 tracking-tight" data-testid="billing-title">{t("billing.title")}</h1>
          <HelpIcon featureTag="billing" portalType="customer" />
        </div>
        <p className="text-sm text-slate-500 mt-1">{t("billing.subtitle")}</p>
      </div>

      {/* Current Plan & Usage */}
      <div className="grid lg:grid-cols-3 gap-4 mb-8">
        <div className="lg:col-span-2 bg-white border border-slate-200 p-6" data-testid="current-plan-card">
          <div className="flex items-start justify-between mb-6">
            <div>
              <div className="text-xs uppercase tracking-[0.2em] text-slate-500 font-medium mb-1">{t("billing.current_plan")}</div>
              <div className="text-2xl font-bold text-slate-900">{displayPlans.find(p => p.current)?.name || "Business"}</div>
              <div className="text-sm text-slate-500 mt-0.5">{(() => { const p = displayPlans.find(x => x.current); return p?.price === 0 ? 'Free' : p?.price ? `$${p.price}` : subscription?.plan_price ? `$${subscription.plan_price}` : 'Custom'; })()}/month · {subscription ? `Plan: ${subscription.plan_name}` : 'Loading...'}</div>
            </div>
            <Badge variant="outline" className="bg-emerald-50 text-emerald-700 border-emerald-200 text-xs uppercase tracking-wider">{t("label.active")}</Badge>
          </div>
          <div className="space-y-4">
            {USAGE_METERS.map((meter, i) => (
              <div key={i} data-testid={`usage-meter-${i}`}>
                <div className="flex items-center justify-between mb-1.5">
                  <span className="text-sm text-slate-700">{meter.label}</span>
                  <span className="text-xs font-mono text-slate-500">{typeof meter.used === 'number' && meter.used > 1000 ? meter.used.toLocaleString() : meter.used} / {typeof meter.total === 'number' && meter.total > 1000 ? meter.total.toLocaleString() : meter.total} {meter.unit}</span>
                </div>
                <Progress value={(meter.used / meter.total) * 100} className="h-2 bg-slate-100" />
              </div>
            ))}
          </div>
          <div className="flex gap-3 mt-6 pt-6 border-t border-slate-100">
            <Button onClick={() => setChangePlanOpen(true)} data-testid="upgrade-plan-btn">
              {t("billing.upgrade")} <ArrowRight className="w-3.5 h-3.5 ml-2" />
            </Button>
            <Button variant="outline" onClick={handleBillingPortal} disabled={portalLoading} data-testid="manage-billing-btn">
              {portalLoading ? <><Loader2 className="w-3.5 h-3.5 mr-2 animate-spin" /> Opening...</> : <><CreditCard className="w-3.5 h-3.5 mr-2" /> {t("billing.payment_method")}</>}
            </Button>
          </div>
        </div>

        {/* Plan Comparison Quick View */}
        <div className="bg-white border border-slate-200 p-6" data-testid="plan-comparison">
          <div className="text-xs uppercase tracking-[0.2em] text-slate-500 font-medium mb-4">Available Plans</div>
          <div className="space-y-3">
            {plansLoading ? (
              <div className="flex items-center justify-center py-6 text-slate-400 gap-2"><Loader2 className="w-4 h-4 animate-spin" /> Loading plans...</div>
            ) : displayPlans.map((plan, i) => (
              <div 
                key={i} 
                className={`p-4 border transition-colors cursor-pointer ${plan.current ? 'border-cyan-200 bg-cyan-50/50' : 'border-slate-100 hover:border-slate-200'}`} 
                onClick={() => { setSelectedPlan(plan); setChangePlanOpen(true); }}
                data-testid={`plan-option-${i}`}
              >
                <div className="flex items-center justify-between mb-1">
                  <span className="text-sm font-semibold text-slate-900">{plan.name}</span>
                  {plan.current && <Badge className="bg-cyan-100 text-cyan-700 text-[10px] border-0">Current</Badge>}
                </div>
                <div className="text-lg font-bold text-slate-900">
                  {plan.price === null ? "Custom" : plan.price === 0 ? "Free" : `$${plan.price}`}
                  <span className="text-xs text-slate-400 font-normal">{plan.price && plan.price > 0 ? "/mo" : ""}</span>
                </div>
                <div className="text-xs text-slate-500">{plan.desc}</div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Payment Methods */}
      <div className="bg-white border border-slate-200 mb-8" data-testid="payment-methods-section">
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-200">
          <div>
            <h3 className="text-sm font-semibold text-slate-900">{t("billing.payment_method")}s</h3>
            <p className="text-xs text-slate-500 mt-0.5">Manage your cards and billing details</p>
          </div>
          <Button size="sm" onClick={() => setAddPaymentOpen(true)} data-testid="add-payment-btn">
            <Plus className="w-4 h-4 mr-2" /> {t("billing.add_payment")}
          </Button>
        </div>
        <div className="divide-y divide-slate-100">
          {paymentMethods.map((pm, i) => (
            <div key={pm.id} className="px-6 py-4 flex items-center justify-between" data-testid={`payment-method-${i}`}>
              <div className="flex items-center gap-4">
                <div className="w-12 h-8 bg-slate-50 border border-slate-200 rounded flex items-center justify-center">
                  {CARD_BRAND_LOGOS[pm.brand] ? (
                    <img src={CARD_BRAND_LOGOS[pm.brand]} alt={pm.brand} className="h-4 object-contain" />
                  ) : (
                    <CreditCard className="w-5 h-5 text-slate-400" />
                  )}
                </div>
                <div>
                  <div className="flex items-center gap-2">
                    <span className="text-sm font-medium text-slate-900 capitalize">{pm.brand}</span>
                    <span className="text-sm text-slate-600">•••• {pm.last4}</span>
                    {pm.isDefault && (
                      <Badge variant="outline" className="text-[10px] bg-blue-50 text-blue-700 border-blue-200">Default</Badge>
                    )}
                  </div>
                  <div className="text-xs text-slate-500">Expires {pm.expMonth}/{pm.expYear}</div>
                </div>
              </div>
              <div className="flex items-center gap-2">
                {!pm.isDefault && (
                  <Button variant="ghost" size="sm" onClick={() => handleSetDefault(pm.id)}>
                    Set as default
                  </Button>
                )}
                <Button variant="ghost" size="sm" className="text-slate-400 hover:text-red-600" onClick={() => handleDeletePayment(pm.id)}>
                  <Trash2 className="w-4 h-4" />
                </Button>
              </div>
            </div>
          ))}
          {paymentMethods.length === 0 && (
            <div className="px-6 py-8 text-center">
              <CreditCard className="w-10 h-10 text-slate-300 mx-auto mb-3" />
              <p className="text-sm text-slate-500">No payment methods added yet</p>
              <Button size="sm" className="mt-3" onClick={() => setAddPaymentOpen(true)}>
                <Plus className="w-4 h-4 mr-2" /> Add Payment Method
              </Button>
            </div>
          )}
        </div>
        <div className="px-6 py-3 bg-slate-50 border-t border-slate-200 flex items-center gap-2">
          <Shield className="w-4 h-4 text-slate-400" />
          <span className="text-xs text-slate-500">Payments are securely processed by</span>
          <span className="text-xs font-bold tracking-tight" style={{ color: "#635BFF" }}>Stripe</span>
        </div>
      </div>

      {/* PQC Metered Usage */}
      <PQCUsageCard token={tokenStore.getAccess()} />

      {/* Invoice History */}
      <div className="bg-white border border-slate-200" data-testid="invoice-history">
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-200">
          <h3 className="text-sm font-semibold text-slate-900">{t("billing.invoices")}</h3>
          <Button variant="outline" size="sm">
            <ExternalLink className="w-4 h-4 mr-2" /> View in Stripe
          </Button>
        </div>
        <div>
          <div className="grid grid-cols-[1fr_110px_110px_120px_90px_44px] bg-slate-50 border-b border-slate-200">
            {["Invoice",t("label.date"),"Plan","Amount",t("label.status"),""].map((h,i) => (
              <div key={i} className="px-4 py-2.5 text-xs font-semibold text-slate-500 uppercase tracking-wide">{h}</div>
            ))}
          </div>
          {invoicesLoading ? (
            <div className="flex items-center justify-center gap-2 text-slate-400 text-sm py-6">
              <Loader2 className="w-4 h-4 animate-spin" /> Loading invoices...
            </div>
          ) : invoices.length === 0 ? (
            <div className="text-center py-8 text-sm text-slate-400">No payment history yet. Upgrade your plan to see invoices here.</div>
          ) : invoices.map((inv, i) => (
            <div key={i} className="grid grid-cols-[1fr_110px_110px_120px_90px_44px] border-b border-slate-100 hover:bg-slate-50/50 transition-colors" data-testid={`invoice-row-${i}`}>
              <div className="px-4 py-3 flex items-center">
                <div>
                  <div className="font-mono text-xs text-slate-900 font-medium">{inv.id}</div>
                  <div className="text-[10px] text-slate-400 font-mono">{inv.session_id?.slice(0, 20)}…</div>
                </div>
              </div>
              <div className="px-4 py-3 text-xs text-slate-600 flex items-center">{inv.date}</div>
              <div className="px-4 py-3 text-xs text-slate-600 flex items-center">{inv.plan}</div>
              <div className="px-4 py-3 font-mono text-xs text-slate-900 font-medium flex items-center">{inv.amount} {inv.currency}</div>
              <div className="px-4 py-3 flex items-center">
                <Badge variant="outline" className={`text-[10px] uppercase tracking-wider ${inv.status === 'Paid' ? 'bg-emerald-50 text-emerald-700 border-emerald-200' : 'bg-amber-50 text-amber-700 border-amber-200'}`}>{inv.status}</Badge>
              </div>
              <div className="px-4 py-3 flex items-center">
                <a href={inv.stripe_url} target="_blank" rel="noopener noreferrer">
                  <Button variant="ghost" size="sm" className="h-8 w-8 p-0" title="View in Stripe" data-testid={`view-invoice-${i}`}>
                    <ExternalLink className="w-3.5 h-3.5" />
                  </Button>
                </a>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Add Payment Method Dialog (Stripe Elements Mockup) */}
      <Dialog open={addPaymentOpen} onOpenChange={setAddPaymentOpen}>
        <DialogContent className="max-w-md" aria-describedby="add-payment-desc">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <CreditCard className="w-5 h-5" /> {t("billing.add_payment")}
            </DialogTitle>
            <DialogDescription id="add-payment-desc">
              Add a new credit or debit card to your account
            </DialogDescription>
          </DialogHeader>
          <div className="py-4 space-y-4">
            {/* Card Preview */}
            <div className="bg-gradient-to-br from-slate-800 to-slate-900 rounded-xl p-5 text-white relative overflow-hidden" data-testid="card-preview">
              <div className="absolute top-0 right-0 w-32 h-32 bg-white/5 rounded-full -mr-16 -mt-16" />
              <div className="flex justify-between items-start mb-8">
                <div className="w-10 h-7 bg-gradient-to-br from-yellow-400 to-yellow-500 rounded-sm" />
                <svg className="h-6" viewBox="0 0 60 25" xmlns="http://www.w3.org/2000/svg"><path fill="#fff" d="M59.64 14.28h-8.06c.19 1.93 1.6 2.55 3.2 2.55 1.64 0 2.96-.37 4.05-.95v3.32a10.1 10.1 0 0 1-4.56 1.02c-4.01 0-6.83-2.5-6.83-7.48 0-4.19 2.39-7.52 6.3-7.52 3.92 0 5.96 3.28 5.96 7.5 0 .4-.04 1.26-.06 1.56zm-5.92-5.62c-1.03 0-2.17.73-2.17 2.58h4.25c0-1.85-1.07-2.58-2.08-2.58zM36.95 5.52c1.55 0 2.66.24 3.82.72v3.73a7.04 7.04 0 0 0-3.47-.84c-1.23 0-1.8.45-1.8 1.08 0 .76 1.06.89 2.39 1.5 1.97.9 2.95 2.09 2.95 4.1 0 3.14-2.17 4.91-5.84 4.91-1.6 0-3.03-.34-4.34-.91v-3.84c1.22.65 2.8 1.13 4.13 1.13 1.22 0 1.83-.42 1.83-1.15 0-.72-.68-.97-1.88-1.46-2.14-.87-3.37-1.91-3.37-4.08 0-2.97 2.26-4.89 5.58-4.89z"/></svg>
              </div>
              <div className="font-mono text-lg tracking-widest mb-4">
                {cardNumber || "•••• •••• •••• ••••"}
              </div>
              <div className="flex justify-between items-end">
                <div>
                  <div className="text-[10px] text-slate-400 uppercase tracking-wider">Card Holder</div>
                  <div className="text-sm font-medium">{cardName || "YOUR NAME"}</div>
                </div>
                <div className="text-right">
                  <div className="text-[10px] text-slate-400 uppercase tracking-wider">Expires</div>
                  <div className="text-sm font-medium">{cardExpiry || "MM/YY"}</div>
                </div>
              </div>
            </div>

            {/* Stripe Elements Mockup */}
            <div className="border border-slate-200 rounded-lg p-4 bg-slate-50">
              <div className="space-y-3">
                <div>
                  <Label className="text-xs text-slate-600">Card number</Label>
                  <Input
                    value={cardNumber}
                    onChange={(e) => setCardNumber(formatCardNumber(e.target.value))}
                    placeholder="1234 5678 9012 3456"
                    maxLength={19}
                    className="mt-1 bg-white font-mono"
                    data-testid="card-number-input"
                  />
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label className="text-xs text-slate-600">Expiry</Label>
                    <Input
                      value={cardExpiry}
                      onChange={(e) => setCardExpiry(formatExpiry(e.target.value))}
                      placeholder="MM/YY"
                      maxLength={5}
                      className="mt-1 bg-white font-mono"
                      data-testid="card-expiry-input"
                    />
                  </div>
                  <div>
                    <Label className="text-xs text-slate-600">CVC</Label>
                    <Input
                      value={cardCvc}
                      onChange={(e) => setCardCvc(e.target.value.replace(/\D/g, '').slice(0, 4))}
                      placeholder="123"
                      maxLength={4}
                      type="password"
                      className="mt-1 bg-white font-mono"
                      data-testid="card-cvc-input"
                    />
                  </div>
                </div>
                <div>
                  <Label className="text-xs text-slate-600">Name on card</Label>
                  <Input
                    value={cardName}
                    onChange={(e) => setCardName(e.target.value.toUpperCase())}
                    placeholder="JOHN DOE"
                    className="mt-1 bg-white"
                    data-testid="card-name-input"
                  />
                </div>
              </div>
            </div>

            <div className="flex items-center gap-2 text-xs text-slate-500">
              <Shield className="w-4 h-4 text-emerald-500" />
              Your payment information is encrypted and secure
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setAddPaymentOpen(false)}>{t("action.cancel")}</Button>
            <Button onClick={handleAddCard} disabled={!cardNumber || !cardExpiry || !cardCvc || !cardName} data-testid="save-card-btn">
              <CreditCard className="w-4 h-4 mr-2" /> Add Card
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Change Plan Dialog */}
      <Dialog open={changePlanOpen} onOpenChange={setChangePlanOpen}>
        <DialogContent className="max-w-3xl" aria-describedby="change-plan-desc">
          <DialogHeader>
            <DialogTitle>Choose Your Plan</DialogTitle>
            <DialogDescription id="change-plan-desc">
              Select a plan that best fits your needs. Changes take effect immediately.
            </DialogDescription>
          </DialogHeader>
          <div className="py-4">
            <div className="grid grid-cols-3 gap-4">
              {displayPlans.map((plan, i) => (
                <div
                  key={plan.id}
                  className={`border rounded-lg p-5 cursor-pointer transition-all ${
                    selectedPlan?.id === plan.id
                      ? 'border-cyan-500 ring-2 ring-cyan-100 bg-cyan-50/30'
                      : plan.current
                      ? 'border-slate-300 bg-slate-50'
                      : 'border-slate-200 hover:border-slate-300'
                  }`}
                  onClick={() => setSelectedPlan(plan)}
                  data-testid={`select-plan-${plan.id}`}
                >
                  <div className="flex items-center justify-between mb-3">
                    <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                      plan.id === 'developer' ? 'bg-blue-100' :
                      plan.id === 'business' ? 'bg-purple-100' : 'bg-amber-100'
                    }`}>
                      {plan.id === 'developer' && <Zap className="w-5 h-5 text-blue-600" />}
                      {plan.id === 'business' && <Building2 className="w-5 h-5 text-purple-600" />}
                      {plan.id === 'enterprise' && <Shield className="w-5 h-5 text-amber-600" />}
                    </div>
                    {plan.current && <Badge className="bg-slate-200 text-slate-700 text-[10px]">Current</Badge>}
                    {selectedPlan?.id === plan.id && !plan.current && (
                      <div className="w-5 h-5 bg-cyan-500 rounded-full flex items-center justify-center">
                        <Check className="w-3 h-3 text-white" />
                      </div>
                    )}
                  </div>
                  <h4 className="font-semibold text-slate-900 mb-1">{plan.name}</h4>
                  <div className="text-2xl font-bold text-slate-900 mb-1">
                    {plan.price === null ? 'Custom' : plan.price === 0 ? 'Free' : `$${plan.price}`}
                    {plan.price > 0 && <span className="text-sm font-normal text-slate-500">/mo</span>}
                  </div>
                  <p className="text-xs text-slate-500 mb-4">{plan.desc}</p>
                  <ul className="space-y-2">
                    {plan.features.map((feature, fi) => (
                      <li key={fi} className="flex items-center gap-2 text-xs text-slate-600">
                        <Check className="w-3.5 h-3.5 text-emerald-500" />
                        {feature}
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setChangePlanOpen(false)}>{t("action.cancel")}</Button>
            <Button
              disabled={!selectedPlan || selectedPlan.current || checkoutLoading}
              onClick={() => { handleUpgrade(selectedPlan?.id); setChangePlanOpen(false); }}
              data-testid="confirm-plan-btn"
            >
              {checkoutLoading && payingPlan === selectedPlan?.id ? (
                <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Redirecting...</>
              ) : !selectedPlan ? 'Select a Plan'
                : selectedPlan.current ? 'Current Plan'
                : selectedPlan?.price === null ? 'Contact Sales'
                : selectedPlan?.price === 0 ? 'Switch to Free'
                : selectedPlan?.price > (displayPlans.find(p => p.current)?.price || 0)
                  ? `${t("billing.upgrade")} to ${selectedPlan?.name}`
                  : `${t("billing.downgrade")} to ${selectedPlan?.name}`}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
